import type { Matter } from '@/lib/services/matters/types';

/** What an Office picker means by *no office* — i.e. the matter runs firm-wide. */
export const NO_OFFICE = '__none__';

/** One selectable office, as both pickers render it. */
export interface MatterOfficeChoice {
  id: number;
  name: string;
}

export interface MatterOfficeOptions {
  /** The office this matter names today, or `null` for firm-wide. */
  assigned: MatterOfficeChoice | null;
  /**
   * Every office the picker may offer, in the firm's own order. **Empty means
   * render no Office control at all** — see below.
   */
  options: MatterOfficeChoice[];
}

/**
 * **Which offices a matter's Office picker may offer** (VALE-549).
 *
 * Two surfaces ask this of the same matter — the Estate details card's inline
 * `Office` row (`matter-attribute-specs.ts`) and the *Manage Estate Details*
 * drawer (`edit-matter-dialog.tsx`) — and the office decides which of the
 * firm's letterheads every letter on the estate prints on. Two copies of the
 * three rules below is exactly the drift the root `CLAUDE.md`
 * bind-to-the-source-of-truth rule exists to prevent, and its symptom would be
 * one surface silently dropping the office the other still shows.
 *
 * The rules:
 *
 * - **The firm has no offices** → `options` is empty and the caller renders
 *   **nothing**, so a single-office firm sees no new chrome anywhere. There is
 *   no free-text fallback: an office is a record, and one that does not exist
 *   cannot be named on a matter.
 * - **The firm has offices** → the server-resolved `matter.offices` (active
 *   only, in the firm's own order). Nothing here re-derives or hardcodes them.
 * - **The matter names an office the list no longer offers** — a *closed*
 *   office — → it is appended, built from the `officeId` + `officeName` the
 *   server resolved off the matter's own row. A picker that cannot re-select
 *   the value it is displaying would turn one edit of any *other* field into a
 *   lost office, and the backend accepts an inactive id for exactly that
 *   reason.
 *
 * Nothing here consults the acting solicitor. A matter with no office runs
 * firm-wide permanently — a valid state, not a gap to be repaired by inferring
 * one from whoever happens to be editing.
 */
export function resolveMatterOfficeOptions(
  matter: Matter | undefined
): MatterOfficeOptions {
  const assigned =
    matter?.officeId != null
      ? {
          id: matter.officeId,
          name: matter.officeName ?? `#${matter.officeId}`
        }
      : null;
  const firmOffices = matter?.offices ?? [];
  const options =
    assigned && !firmOffices.some((office) => office.id === assigned.id)
      ? // The closed office this matter already names, kept choosable.
        [...firmOffices, assigned]
      : firmOffices;

  return { assigned, options };
}
